(ns frontend.components.open-search-select
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.opensearch.entity-indices :as entity-indices]
   [common.opensearch.fields :as cos]
   [common.schema.field :as field]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.entity-types :as entity-types]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.conn :as conn]
   [frontend.state.current-session :as session]
   [frontend.view.entity :as f-entity]
   [promesa.core :as p]
   [reagent.core :as r]
   [ui.components.select.async-virtualized :as async-select]))

(def options-cache* (r/atom {}))

(defn create-start-value
  "What a newly created entity starts with when it is created from a picker.

   Defaults to the typed text, which `reset-state!` turns into the new entity's
   label. A field may declare `:field/create-start-value-fn` to seed more than
   that - typically the link back to the entity being edited, so the new entity
   arrives already attached instead of asking the user to re-state where it
   belongs.

   The fn arrives already bound to its containing entity by whichever modal is
   hosting the field, the same way `:field/option-filter-fn` and
   `:field/os-filter-terms-fn` do. Binding it there rather than looking it up
   here is what lets the creator modal, which has only a draft entity, supply
   the same context the editor modal supplies from a saved one."
  [args query]
  (let [seed-fn (:field/create-start-value-fn args)]
    (if (fn? seed-fn)
      (seed-fn {:query query})
      query)))


(defn open-search!
  [{:keys [containing-entity creation-pipeline-id named-value-attribute field/os-filter-terms-fn
           search-query success-callback! entity-types start-index page-size
           excluded-ids] :as ctx}]

  (let [pipeline-id (or (pipeline/containing-pipeline-id containing-entity)
                        creation-pipeline-id)
        pipeline-attribute? (and pipeline-id
                                 (every? #{:entity.type/stage :entity.type/named-value} entity-types)
                                 (registry/pipeline-attribute? named-value-attribute))
        filter-terms (apply
                      merge
                      {}
                      (when (fn? os-filter-terms-fn)
                        (os-filter-terms-fn ctx))
                      (when named-value-attribute
                        {"original.named-value/attribute.keyword" (cu/kw->str named-value-attribute)})
                      (when (and pipeline-id
                                 (or (every? #{:entity.type/stage :entity.type/opportunity-stage :entity.type/sales-opportunity-stage :entity.type/exit-opportunity-stage} entity-types)
                                     (and (every? #{:entity.type/named-value} entity-types)
                                          (registry/pipeline-attribute? named-value-attribute))))
                        {"extras.pipeline" pipeline-id})
                      (when (and named-value-attribute
                                 (not pipeline-attribute?))
                        {"extras.archived" false}))]
    ;;(js/console.log "Open search : " filter-terms)
    (-> (conn/sendp! :search/universal-search
                     {:query        search-query
                      :entity-types (vec entity-types)
                      :sort-terms   (if named-value-attribute
                                      [{"original.common/ord" {:order "asc"}}]
                                      [{"extras.lower-case-display-name.keyword" {:order "asc"}}])
                      :filter-terms filter-terms})
        (p/then
         (fn [{:keys [body]}]
           (let [response (-> body
                              :responses
                              first)
                 total-hit-count (-> response
                                     :hits
                                     :total
                                     :value)
                 hits (-> response
                          :hits
                          :hits)
                 has-next-page? (and (number? page-size)
                                     (number? start-index)
                                     (= (count hits) page-size)
                                     (< (+ start-index page-size) total-hit-count))
                 options (->> hits
                              (map (fn [hit]
                                     {:value (-> hit :_id (js/parseInt))
                                      :icon  (when (> (count entity-types) 1)
                                               (-> hit :_source :original :entity/type
                                                   (cu/str->keyword-if-string)
                                                   (entity-types/resolve-icon)))
                                      :label (str                                                   ;_(or (-> hit :_score) "0")
                                              ;" "
                                              (cos/hit->entity-name hit))})))
                 ;; Cache the raw OS options (label lookup by id shouldn't
                 ;; miss just because an id is filtered out of the visible
                 ;; menu — e.g. a persisted value the caller still needs to
                 ;; render).
                 _ (reset! options-cache*
                           (merge @options-cache*
                                  (->> options
                                       (map (fn [o]
                                              [(:value o) o]))
                                       (into {}))))
                 visible-options (if (seq excluded-ids)
                                   (filterv (fn [o]
                                              (not (contains? excluded-ids (:value o))))
                                            options)
                                   options)
                 page {:options visible-options
                       :has-next-page? (boolean has-next-page?)}]
             (when (fn? success-callback!)
               (success-callback! visible-options))
             page))))))

(defn local-search
  [{:keys [search-query entity-types success-callback! excluded-ids] :as args}]
  (let [options (->> (session/local-search (assoc args :entity-types entity-types
                                                       :search-query search-query))
                     (mapv (fn [e]
                             {:value (:db/id e)
                              :label (entity/display-string e)})))
        visible-options (if (seq excluded-ids)
                          (filterv (fn [o]
                                     (not (contains? excluded-ids (:value o))))
                                   options)
                          options)
        page {:options visible-options
              :has-next-page? false}]
    (when (fn? success-callback!)
      (success-callback! visible-options))
    page))

(defn get-options
  [{:keys [entity-types] :as args}]
  (if (every? entity-indices/is-search-indexed? entity-types)
    (open-search! args)
    (local-search args)))


(defn get-option-label
  [value]
  (let [value (or (:value value)
                  (when (number? value)
                    value)
                  value)]
    (cond (number? value)
          (-> (conn/sendp! :search/get-label
                           {:db/id value})
              (p/then
               (fn [{:keys [body]}]
                 (reset! options-cache*
                         (merge @options-cache*
                                {value {:value value
                                        :label body}}))
                 body)))

          (and (coll? value)
               (every? number? value))
          (-> (conn/sendp! :search/get-labels
                           {:ids (set value)})
              (p/then
               (fn [{:keys [body]}]
                 (reset! options-cache*
                         (merge @options-cache*
                                (->> body
                                     (map (fn [[value label]]
                                            [value {:value value
                                                    :label label}]))
                                     (into {}))))
                 body))))))

(defn resolve-value
  [value]
  (cond (nil? value)
        nil
        (string? value)
        {:value value
         :label value}
        (and (map? value)
             (:entity/type value))
        {:value (:db/id value)
         :label (entity/display-string value)}
        :else
        (or (get @options-cache* value)                                                             ;; TODO -> get label from cache
            (do (get-option-label value)
                {:value value
                 :label (loc/tr :loading)}))))

(defn resolve-values
  [values]
  (->> values
       (mapv resolve-value)
       (seq)))

(defn render-async-select-multi
  [{:keys [_on-blur! _value _on-change! _on-menu-open! _on-menu-close! _creation-pipeline-id]}]
  (let [state* (r/atom 0)
        id-str (gensym "opensearch_m_")]
    (fn [{:keys [on-blur! value on-change! on-menu-open! on-menu-close! creation-pipeline-id] :as args}]
      (let [entity-types (field/select-entity-types args)
            create-entity-types (field/create-entity-types args)
            change-value! (fn [selected-values _selected-options]
                            (when (fn? on-change!)
                              (on-change! nil selected-values #()))
                            (when (fn? on-blur!)
                              (on-blur! nil selected-values #())))
            create-on-fly? (and (:create-on-fly? args)
                                (seq create-entity-types))
            resolved-value (resolve-values value)]
        ^{:key id-str}
        [async-select/view
         {:value               resolved-value
          :on-change!          change-value!
          :max-hits-per-page   50
          :get-options         (fn [input callback]
                                 (get-options (assoc args
                                                     :entity-types entity-types
                                                     :search-query input
                                                     :success-callback! callback)))
          :on-menu-open!       on-menu-open!
          :on-menu-close!      on-menu-close!
          :auto-focus?         false
          :is-clearable?       (-> args :field/not-clearable? (not))
          :is-multi?           true
          :open-menu-on-focus? true
          :create!             (when create-on-fly?
                                 (fn [query]
                                   (f-entity/open-creator!
                                    create-entity-types
                                    {:named-value-attribute (:named-value-attribute args)
                                     :start-value           (create-start-value args query)
                                     :attribute             (:attribute args)
                                     :skip-success-msg?     true
                                     :creation-pipeline-id  creation-pipeline-id
                                     :on-ok!                (fn [{:keys [new-entity-id close-cb!]}]
                                                              (reset! state* (inc @state*))
                                                              (change-value! (set (conj (or value #{}) new-entity-id))
                                                                             (concat resolved-value [{:value new-entity-id
                                                                                                      :label query}]))
                                                              (when (fn? close-cb!)
                                                                (close-cb!)))})))}]))))

(defn render-async-select-single
  [{:keys [_on-blur! _on-change! _value _on-menu-open! _on-menu-close! _creation-pipeline-id _auto-focus?]}]
  (let [state* (r/atom 0)
        id-str (gensym "opensearch_s_")]
    (fn [{:keys [on-blur! on-change! value on-menu-open! on-menu-close! creation-pipeline-id auto-focus?] :as args}]
      (let [entity-types (field/select-entity-types args)
            create-entity-types (field/create-entity-types args)
            change-value! (fn [value _option]
                            (when (fn? on-change!)
                              (on-change! nil value #()))
                            (when (fn? on-blur!)
                              (on-blur! nil value #())))
            create-on-fly? (and (:create-on-fly? args)
                                (seq create-entity-types))
            resolved-value (resolve-value value)]
        ^{:key id-str}
        [async-select/view
         {:value               resolved-value
          :on-change!          change-value!
          :on-menu-open!       on-menu-open!
          :on-menu-close!      on-menu-close!
          :max-hits-per-page   50
          :get-options         (fn [input callback]
                                 (get-options (assoc args
                                                     :entity-types entity-types
                                                     :search-query input
                                                     :success-callback! callback)))
          :auto-focus?         auto-focus?
          :is-clearable?       (-> args :field/not-clearable? (not))
          :is-multi?           false
          :open-menu-on-focus? true
          :create!             (when create-on-fly?
                                 (fn [query]
                                   (f-entity/open-creator!
                                    create-entity-types
                                    {:named-value-attribute (:named-value-attribute args)
                                     :start-value           (create-start-value args query)
                                     :skip-success-msg?     true
                                     :attribute             (:attribute args)
                                     :creation-pipeline-id  creation-pipeline-id
                                     :on-ok!                (fn [{:keys [new-entity-id close-cb!]}]
                                                              (reset! state* (inc @state*))
                                                              (change-value! new-entity-id {:value new-entity-id
                                                                                            :label query})
                                                              (when (fn? close-cb!)
                                                                (close-cb!)))})))}]))))
